<template>
  <div id="runningTrack" class="margin-subpages">
    <div class="pt-24 px-20 pb-16 hv-chart-card">
      <div class="sub-title px-12">
        <span class="fs-24" style="text-transform: lowercase"
          >{{ $utils("addCommasInNumber", distance_all) }}
          {{ $t("happilyVillage.km") }}</span
        >
        {{ $t("happilyVillage.Distance_Traveled") }}
      </div>
      <div class="relative" style="height: 240px">
        <div
          class="center absolute-mid"
          v-if="loadingChartStatus"
        >
          <loading-even></loading-even>
        </div>
        <canvas
          ref="runnginTrackBarchart"
          height="240px"
          class="barchart-style"
        ></canvas>
      </div>
    </div>

    <div class="rt-leaderboard mt-32">
      <div class="flex-row-between align-items-center rt-header">
        <div class="sub-title">
          {{ $t("happilyVillage.Running_Track_Leaderboard") }}
        </div>
      </div>
      <div class="mt-16">
        <div class="rt-toolbar mb-16">
          <el-input
            @input="typeingSearchRunningTrack"
            class="rt-search"
            :placeholder="$t('global.Search')"
            v-model="inputSearchRunnginTrack"
            clearable
          >
            <i slot="prefix" class="el-input__icon el-icon-search"></i>
          </el-input>

          <div class="rt-toolbar-spacer"></div>

          <!-- Scope pill -->
          <div class="header-pill-dropdown" :class="{ active: !isDefaultScope }">
            <el-dropdown trigger="click" placement="bottom-end" :hide-on-click="false" @command="onScopeCommand" ref="scopeDropdown">
              <div class="flex-mid" style="color: inherit; font-weight: inherit;">
                <span class="rt-scope-ico">
                  <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true">
                    <path fill="currentColor" d="M19 4h-1V2h-2v2H8V2H6v2H5C3.89 4 3 4.9 3 6v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V10h14v10zM5 8V6h14v2H5z"/>
                  </svg>
                </span>
                <span class="rt-scope-label">{{ scopeLabel }}</span>
                <i class="el-icon-arrow-down"></i>
              </div>
              <!-- popper-class on <el-dropdown> is silently ignored in Element UI
                   2.15.9 (no popperClass prop); the class must be merged onto the
                   menu's root <ul> instead, so it goes on <el-dropdown-menu>. -->
              <el-dropdown-menu slot="dropdown" class="happily-dropdown-popper rt-scope-popper">
                <div class="rt-scope-section-label">{{ $t('happilyVillage.Quick_ranges') }}</div>
                <el-dropdown-item
                  v-for="p in presetItems"
                  :key="p.value"
                  :command="{ kind: 'preset', value: p.value }"
                  :class="{ 'rt-scope-item-selected': scope.kind === 'preset' && scope.value === p.value }"
                >
                  {{ p.label }}
                </el-dropdown-item>
                <div class="rt-scope-divider"></div>
                <div class="rt-scope-section-label">{{ $t('happilyVillage.Specific_month') }}</div>
                <div class="rt-month-picker">
                  <div class="rt-year-bar">
                    <span class="rt-year-arrow" @click="monthPickerYear -= 1">&#8249;</span>
                    <span class="rt-year-label">{{ monthPickerYear }}</span>
                    <span class="rt-year-arrow" :class="{ disabled: monthPickerYear >= currentYear }" @click="onMonthPickerYearForward">&#8250;</span>
                  </div>
                  <div class="rt-month-grid">
                    <div
                      v-for="(m, idx) in monthShortLabels"
                      :key="idx"
                      class="rt-month-cell"
                      :class="monthCellClass(idx + 1)"
                      @click="onMonthPick(idx + 1)"
                    >
                      {{ m }}
                    </div>
                  </div>
                </div>
              </el-dropdown-menu>
            </el-dropdown>
          </div>

          <!-- Export -->
          <button
            type="button"
            class="rt-export-btn"
            @click="exportCurrentScope()"
            :title="$t('happilyVillage.Export_this_view')"
            :disabled="loading"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <path fill="currentColor"
                d="m12 16l-5-5l1.4-1.45l2.6 2.6V4h2v8.15l2.6-2.6L17 11zm-6 4q-.825 0-1.412-.587T4 18v-3h2v3h12v-3h2v3q0 .825-.587 1.413T18 20z" />
            </svg>
            <span>{{ $t('happilyVillage.Export_this_view') }}</span>
          </button>
        </div>

        <el-table
          @sort-change="onSortChange"
          class="table-happily-village rt-table mt-16"
          :data="currentScopeData.dataTable"
          style="width: 100%"
          :empty-text="loading ? ' ' : (inputSearchRunnginTrack === '' ? 'No Running Track Activity' : 'No results found...')"
        >
          <el-table-column
            :label="$t('happilyVillage.Rank')"
            sortable="custom"
            align="center"
            width="96"
            prop="rank"
          >
            <template slot-scope="scope">
              <div class="rank-cell" :class="rankClass(scope.row.rank)">
                <span v-if="scope.row.rank <= 3" class="rank-medal">
                  <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                    <path fill="currentColor" d="M12 2l2.39 4.84L20 7.66l-4 3.9.94 5.5L12 14.77l-4.94 2.29L8 11.56 4 7.66l5.61-.82L12 2z"/>
                  </svg>
                </span>
                <span v-else class="rank-num">{{ scope.row.rank }}</span>
              </div>
            </template>
          </el-table-column>

          <el-table-column
            sortable="custom"
            :label="$t('happilyVillage.Name')"
            min-width="260"
            prop="name"
          >
            <template slot-scope="scope">
              <div class="name-cell">
                <span class="runner-name">{{ scope.row.name }}</span>
                <div class="booster-row">
                  <span
                    class="booster-chip"
                    :class="scope.row.booster_key !== undefined ? 'booster-chip--active' : 'booster-chip--empty'"
                  >
                    <span class="booster-dot"></span>
                    {{ scope.row.booster_key === undefined ? "No active booster" : scope.row.booster_name }}
                  </span>
                  <span
                    v-if="showDistanceDiff(scope.row)"
                    class="distance-diff"
                    :class="getDistanceDiffClass(scope.row.distance, scope.row.distance_main)"
                  >
                    {{ getDistanceDiff(scope.row.distance, scope.row.distance_main) }}
                  </span>
                </div>
              </div>
            </template>
          </el-table-column>

          <el-table-column
            sortable="custom"
            :label="$t('happilyVillage.Distance')"
            width="220"
            prop="distance"
          >
            <template slot-scope="scope">
              <div class="distance-cell">
                <span class="distance-value">{{ renderDistanceData(scope.row.distance) }}</span>
                <span class="distance-unit">km</span>
              </div>
            </template>
          </el-table-column>

          <el-table-column
            v-if="showRealDistanceColumn"
            sortable="custom"
            label="Real Distance"
            width="200"
            prop="distance_main"
          >
            <template slot-scope="scope">
              <div class="distance-cell distance-cell--muted">
                <span class="distance-value">{{ renderDistanceData(scope.row.distance_main || 0) }}</span>
                <span class="distance-unit">km</span>
              </div>
            </template>
          </el-table-column>
        </el-table>

        <div class="center mt-20 mb-40 pt-20 pb-40" v-if="loading">
          <loading-even></loading-even>
        </div>

        <div class="flex-center pagination mt-10">
          <el-pagination
            @current-change="onPageChange"
            :current-page="currentScopeData.page"
            :page-size="10"
            :pager-count="5"
            layout="prev, pager, next"
            :total="currentScopeData.results"
          ></el-pagination>
          <span class="results-status">
            {{ currentScopeData.dataTable.length }}
            {{ $t("happilyVillage.results_from") }}
            {{ $utils("addCommasInNumber", currentScopeData.results) }}
            {{ $t("happilyVillage.results") }}
          </span>
        </div>
      </div>
    </div>
    <download-progress :active="downloadRunningTrackBar" wording="Preparing export, please wait..." />
  </div>
</template>

<script>
import Chart from "chart.js/auto";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { mapGetters } from "vuex";
import { buildHVBarOptions, buildHVBarDataset } from "./helpers/chart-defaults.js";

const PRESETS = [
  { value: 'today',     i18n: 'Today' },
  { value: 'thisWeek',  i18n: 'This_week' },
  { value: 'thisMonth', i18n: 'This_month' },
  { value: 'lastMonth', i18n: 'Last_Month' },
  { value: 'allTime',   i18n: 'All_time' },
];

function parseScopeParam(raw) {
  if (!raw) return null;
  const [kind, rest] = raw.split(':');
  if (kind === 'preset') {
    const valid = PRESETS.map(p => p.value);
    if (valid.indexOf(rest) === -1) return null;
    return { kind: 'preset', value: rest };
  }
  if (kind === 'month') {
    const m = /^(\d{4})-(\d{1,2})$/.exec(rest || '');
    if (!m) return null;
    const year = parseInt(m[1], 10);
    const month = parseInt(m[2], 10);
    if (month < 1 || month > 12) return null;
    return { kind: 'month', year, month };
  }
  return null;
}

function scopeToParam(scope) {
  if (scope.kind === 'preset') return `preset:${scope.value}`;
  if (scope.kind === 'month') return `month:${scope.year}-${String(scope.month).padStart(2, '0')}`;
  return '';
}

function scopesEqual(a, b) {
  if (!a || !b || a.kind !== b.kind) return false;
  if (a.kind === 'preset') return a.value === b.value;
  if (a.kind === 'month') return a.year === b.year && a.month === b.month;
  return false;
}

export default {
  name: "runningTrack",
  components: {
    loadingEven: require("../even/loading").default,
    downloadProgress: require("../even/download-progress").default,
  },
  created() {
    // getRunningTrackAll (all-time) is only needed for the allTime scope;
    // loadScope will fetch it lazily if/when that scope is selected.
    this.$store.dispatch("happilyvillage/getDataRunningTrack");
    let fromUrl = parseScopeParam(this.$route.query.scope);
    if (fromUrl && fromUrl.kind === 'month') {
      const now = new Date();
      const isFuture = fromUrl.year > now.getFullYear() ||
        (fromUrl.year === now.getFullYear() && fromUrl.month > now.getMonth() + 1);
      if (isFuture) {
        fromUrl = null;  // fall through to default scope
        this.$router.replace({ query: { ...this.$route.query, scope: undefined } }).catch(() => {});
        this.$message.warning('That month is in the future — showing This month instead.');
      }
    }
    if (fromUrl) {
      this.$store.commit("happilyvillageRunningtrack/setScope", fromUrl);
      // watch.scope will fire loadScope
    } else {
      this.$store.dispatch("happilyvillageRunningtrack/loadScope");
    }
    // Ensure picker year syncs to a deep-linked month scope:
    if (this.scope.kind === 'month') {
      this.monthPickerYear = this.scope.year;
    }
  },
  watch: {
    runningtrack_graphmonth() {
      if (this.running_track_bar_chart) this.running_track_bar_chart.destroy();
      this.randerBarChartRunningTrack();
    },
    scope: {
      handler(newScope) {
        if (newScope.kind === 'month') {
          this.monthPickerYear = newScope.year;
        }
        const param = scopeToParam(newScope);
        const current = this.$route.query.scope || '';
        if (param !== current) {
          this.$router.replace({ query: { ...this.$route.query, scope: param } }).catch(() => {});
        }
        this.$store.dispatch("happilyvillageRunningtrack/loadScope");
      },
      deep: true,
    },
  },
  computed: {
    ...mapGetters("happilyvillageRunningtrack", {
      scope: "scope",
      loading: "loading",
      currentScopeData: "currentScopeData",
      currentScopeFilteredRows: "currentScopeFilteredRows",
    }),
    runningtrack_graphmonth() {
      return this.$store.getters["happilyvillage/runningtrack_graphmonth16"];
    },
    distance_all() {
      return this.$store.getters["happilyvillage/distance_all16"];
    },
    isDefaultScope() {
      return scopesEqual(this.scope, { kind: 'preset', value: 'thisMonth' });
    },
    presetItems() {
      return PRESETS.map(p => ({ value: p.value, label: this.$t(`happilyVillage.${p.i18n}`) }));
    },
    scopeLabel() {
      if (this.scope.kind === 'preset') {
        const p = PRESETS.find(x => x.value === this.scope.value);
        return p ? this.$t(`happilyVillage.${p.i18n}`) : '';
      }
      return `${this.monthShortLabels[this.scope.month - 1]} ${this.scope.year}`;
    },
    monthShortLabels() {
      const m = this.$t('happilyVillage.months_short');
      return ['jan','feb','mar','apr','may','jun','jul','aug','sep','oct','nov','dec'].map(k => m[k]);
    },
    showRealDistanceColumn() {
      return this.currentScopeFilteredRows.some(item => item.distance_main !== undefined);
    },
  },
  data() {
    const now = new Date();
    return {
      inputSearchRunnginTrack: "",
      typingSearch: null,
      downloadRunningTrackBar: false,
      loadingChartStatus: true,
      monthPickerYear: now.getFullYear(),
      currentYear: now.getFullYear(),
      currentMonth: now.getMonth() + 1,
    };
  },
  mounted() {
    if (this.running_track_bar_chart) this.running_track_bar_chart.destroy();
    this.randerBarChartRunningTrack();
  },
  beforeDestroy() {
    clearTimeout(this.typingSearch);
    if (this.running_track_bar_chart) {
      this.running_track_bar_chart.destroy();
      this.running_track_bar_chart = null;
    }
  },
  methods: {
    renderDistanceData(distance) {
      return this.$utils("addCommasInNumber", (distance / 1000).toFixed(2));
    },
    showDistanceDiff(row) {
      return row.booster_key !== undefined && row.distance && row.distance_main;
    },
    onScopeCommand(scope) {
      this.$store.commit("happilyvillageRunningtrack/setScope", scope);
      if (this.$refs.scopeDropdown) this.$refs.scopeDropdown.hide();
    },
    onMonthPick(month) {
      if (this.monthPickerYear === this.currentYear && month > this.currentMonth) return;
      this.onScopeCommand({ kind: 'month', year: this.monthPickerYear, month });
    },
    onMonthPickerYearForward() {
      if (this.monthPickerYear < this.currentYear) this.monthPickerYear += 1;
    },
    monthCellClass(month) {
      const isActive = this.scope.kind === 'month' && this.scope.year === this.monthPickerYear && this.scope.month === month;
      const isFuture = this.monthPickerYear === this.currentYear && month > this.currentMonth;
      return {
        'rt-month-cell--active': isActive,
        'rt-month-cell--disabled': isFuture,
      };
    },
    onPageChange(page) {
      this.$store.commit("happilyvillageRunningtrack/setPage", page);
    },
    onSortChange({ column, order }) {
      if (order) {
        this.$store.commit("happilyvillageRunningtrack/setSort", { field: column.property, order });
      }
    },
    typeingSearchRunningTrack(value) {
      const searchValue = value === undefined ? this.inputSearchRunnginTrack : value;
      clearTimeout(this.typingSearch);
      this.typingSearch = setTimeout(() => {
        this.$store.commit("happilyvillageRunningtrack/setInputSearching", searchValue);
      }, this.variable.typingTimeout);
    },
    rankClass(rank) {
      if (rank === 1) return 'rank-cell--gold';
      if (rank === 2) return 'rank-cell--silver';
      if (rank === 3) return 'rank-cell--bronze';
      return '';
    },
    getDistanceDiffClass(distance, distance_main) {
      const diff = distance - distance_main;
      if (diff > 0) return 'distance-gain';
      if (diff < 0) return 'distance-loss';
      return '';
    },
    getDistanceDiff(distance, distance_main) {
      const diff = distance - distance_main;
      const diffKm = Math.abs(diff / 1000);
      const sign = diff > 0 ? '+' : diff < 0 ? '-' : '';
      return diffKm > 0 ? `(${sign}${this.$utils("addCommasInNumber", diffKm.toFixed(2))} km)` : '';
    },
    async exportCurrentScope() {
      this.downloadRunningTrackBar = true;
      try {
        const Excel = require("exceljs");
        const wb = new Excel.Workbook();

        // Reuse the store's filtered + distance-ranked rows so search / sort
        // semantics stay in lock-step with the table.
        const scope = this.scope;
        const rows = this.currentScopeFilteredRows;
        const hasRealDistanceCol = this.showRealDistanceColumn;

        const sheetTitle = this.scopeLabel;
        const ws = wb.addWorksheet(sheetTitle.substring(0, 31)); // Excel sheet name limit

        const lastColumn = hasRealDistanceCol ? "E2" : "D2";
        ws.mergeCells("B2", lastColumn);
        ws.getRow(2).height = 25;
        ws.getCell("B2").font = { name: "Arial", size: 18, bold: true };
        ws.getCell("B2").alignment = { vertical: "middle", horizontal: "center" };
        ws.getCell("B2").value = `${sheetTitle} Running Track Leaderboard`;

        let headerRow = 4;
        if (this.inputSearchRunnginTrack && this.inputSearchRunnginTrack.trim()) {
          const lastColumnFilter = hasRealDistanceCol ? "E3" : "D3";
          ws.mergeCells("B3", lastColumnFilter);
          ws.getCell("B3").font = { name: "Arial", size: 12, italic: true };
          ws.getCell("B3").alignment = { vertical: "middle", horizontal: "center" };
          ws.getCell("B3").value = `Filtered by: "${this.inputSearchRunnginTrack.trim()}"`;
          headerRow = 5;
        }

        ws.getColumn("A").width = 5;
        ws.getColumn("B").width = 10;
        ws.getColumn("C").width = 30;
        ws.getColumn("D").width = 15;
        if (hasRealDistanceCol) ws.getColumn("E").width = 15;

        const header = ["", "Rank", "Name", "Distance (km)"];
        if (hasRealDistanceCol) header.push("Real Distance (km)");
        ws.getRow(headerRow).values = header;

        rows.forEach((item, index) => {
          const r = ["", index + 1, item.name || "-", this.renderDistanceData(item.distance || 0)];
          if (hasRealDistanceCol) r.push(this.renderDistanceData(item.distance_main || 0));
          ws.addRow(r);
        });

        ws.eachRow(function (row) {
          if (row.number === headerRow) {
            row.eachCell(function (cell) {
              ws.getCell(cell.address).font = { name: "Arial", size: 11, bold: true };
              ws.getCell(cell.address).fill = {
                type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE6E6E6' },
              };
            });
          }
        });

        const buffer = await wb.xlsx.writeBuffer({ base64: true });
        const a = document.createElement("a");
        const blob = new Blob([buffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = URL.createObjectURL(blob);
        a.href = url;

        const now = new Date();
        const ts = now.getFullYear() + '-' +
                   String(now.getMonth() + 1).padStart(2, '0') + '-' +
                   String(now.getDate()).padStart(2, '0') + '_' +
                   String(now.getHours()).padStart(2, '0') + '-' +
                   String(now.getMinutes()).padStart(2, '0');
        const slug = scope.kind === 'preset'
          ? scope.value.replace(/([A-Z])/g, '_$1').replace(/^_/, '')
          : `${scope.year}-${String(scope.month).padStart(2, '0')}`;
        a.download = `Running_Track_${slug}_${ts}.xlsx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);

        this.$message.success(this.$t('happilyVillage.Export_complete'));
      } catch (error) {
        console.error('Export failed:', error);
        this.$message.error('Export failed. Please try again.');
      } finally {
        this.downloadRunningTrackBar = false;
      }
    },
    randerBarChartRunningTrack() {
      var runnginTrackBarchart = this.$refs.runnginTrackBarchart;
      const months = [];
      const value = [];
      for (const x in this.runningtrack_graphmonth) {
        months.push(this.runningtrack_graphmonth[x].month);
        value.push(this.runningtrack_graphmonth[x].value);
      }
      months.reverse(); value.reverse();
      if (runnginTrackBarchart) {
        this.loadingChartStatus = false;
        const runningTrackChartData = {
          labels: months,
          datasets: [{ label: "Distance Traveleds", data: value, ...buildHVBarDataset('runningtrack') }],
        };
        this.running_track_bar_chart = new Chart(
          runnginTrackBarchart.getContext("2d"),
          {
            plugins: [ChartDataLabels],
            type: "bar",
            data: runningTrackChartData,
            options: buildHVBarOptions('runningtrack', {
              datalabelFormatter: function(value) {
                let str = value.toString().split(".");
                if (str[0].length >= 4) str[0] = str[0].replace(/(\d)(?=(\d{3})+$)/g, "$1,");
                str = str[0]; if (str == "0") str = "";
                return str;
              },
            }),
          }
        );
      }
    },
  },
};
</script>

<style lang="scss" scoped>
$primary: #6c30cc;
$ink: #1a1a2e;
$muted: rgba(#1a1a2e, 0.55);
$line: rgba(#1a1a2e, 0.08);

.barchart-style {
  border-radius: 4px;
}
.sub-title {
  text-transform: capitalize;
}
.bg-black-001 {
  background-color: rgba(0, 0, 0, 0.01);
}

/* ----- Toolbar: search + scope + export ----- */
.rt-toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
}
.rt-search {
  width: 260px;
  ::v-deep .el-input__inner {
    height: 38px;
    line-height: 38px;
    border-radius: 10px;
    border: 1px solid $line;
    background: #fff;
    transition: border-color .2s ease, box-shadow .2s ease;
    padding-left: 34px;
    font-size: 13px;
  }
  ::v-deep .el-input__inner::placeholder {
    color: rgba(#1a1a2e, 0.4);
  }
  ::v-deep .el-input__inner:focus {
    border-color: $primary;
    box-shadow: 0 0 0 3px rgba(108, 48, 204, 0.12);
  }
  ::v-deep .el-input__icon.el-icon-search {
    color: rgba(#1a1a2e, 0.45);
    font-size: 15px;
    line-height: 38px;
  }
  ::v-deep .el-input__prefix {
    left: 10px;
  }
}
.rt-export-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 38px;
  padding: 0 14px;
  border-radius: 10px;
  background: #fff;
  border: 1px solid $line;
  color: $ink;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all .2s ease;
  outline: none;

  svg { color: rgba(#1a1a2e, 0.65); transition: color .2s ease; }

  &:hover {
    border-color: rgba(108, 48, 204, 0.4);
    color: $primary;
    background: rgba(108, 48, 204, 0.04);
    svg { color: $primary; }
  }
  &:active {
    transform: translateY(1px);
  }
}

/* ----- Table polish ----- */
.rt-table {
  font-size: 13px;

  ::v-deep .el-table__header-wrapper th {
    background: transparent !important;
  }
  ::v-deep .el-table__header-wrapper th .cell {
    font-size: 12px !important;
    font-weight: 600 !important;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: rgba(#1a1a2e, 0.5) !important;
  }
  ::v-deep .el-table__row td {
    padding: 14px 0 !important;
    border-bottom: 1px solid $line !important;
  }
  ::v-deep .el-table__row:hover > td {
    background: rgba(108, 48, 204, 0.03) !important;
  }
  ::v-deep .el-table::before {
    display: none;
  }
}

/* ----- Rank cell ----- */
.rank-cell {
  width: 40px;
  height: 40px;
  margin: 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: rgba(#1a1a2e, 0.04);
  color: rgba(#1a1a2e, 0.6);
  font-weight: 600;
  font-size: 14px;
  transition: transform .2s ease, box-shadow .2s ease;

  .rank-num { line-height: 1; }
  .rank-medal { display: inline-flex; align-items: center; justify-content: center; line-height: 0; }
}
.rank-cell--gold {
  background: linear-gradient(135deg, #FFE6A0 0%, #F5B800 100%);
  color: #8B5A00;
  box-shadow: 0 2px 6px rgba(245, 184, 0, 0.35);
}
.rank-cell--silver {
  background: linear-gradient(135deg, #F1F2F4 0%, #B8BDC6 100%);
  color: #5A6270;
  box-shadow: 0 2px 6px rgba(160, 168, 180, 0.3);
}
.rank-cell--bronze {
  background: linear-gradient(135deg, #F6C89A 0%, #C87533 100%);
  color: #5C3211;
  box-shadow: 0 2px 6px rgba(200, 117, 51, 0.3);
}
::v-deep .el-table__row:hover .rank-cell {
  transform: scale(1.06);
}

/* ----- Name + booster cell ----- */
.name-cell {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.runner-name {
  font-size: 14px;
  font-weight: 600;
  color: $ink;
  letter-spacing: -0.01em;
}
.booster-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.booster-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 22px;
  padding: 0 10px;
  border-radius: 999px;
  font-size: 11.5px;
  font-weight: 500;
  letter-spacing: 0.01em;
  line-height: 1;
  white-space: nowrap;

  .booster-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    flex: 0 0 auto;
  }
}
.booster-chip--active {
  background: rgba(76, 175, 80, 0.12);
  color: #2E7D32;
  .booster-dot {
    background: #4CAF50;
    box-shadow: 0 0 0 3px rgba(76, 175, 80, 0.18);
  }
}
.booster-chip--empty {
  background: rgba(#1a1a2e, 0.04);
  color: rgba(#1a1a2e, 0.45);
  .booster-dot {
    background: rgba(#1a1a2e, 0.25);
  }
}

/* ----- Distance cell ----- */
.distance-cell {
  display: inline-flex;
  align-items: baseline;
  gap: 4px;
  color: $ink;

  .distance-value {
    font-size: 16px;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    letter-spacing: -0.01em;
  }
  .distance-unit {
    font-size: 11px;
    font-weight: 500;
    color: rgba(#1a1a2e, 0.5);
    text-transform: uppercase;
    letter-spacing: 0.06em;
  }
}
.distance-cell--muted {
  .distance-value { color: rgba(#1a1a2e, 0.55); font-weight: 500; }
}

/* ----- Distance diff (booster delta) ----- */
.distance-diff {
  font-size: 11px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  border-radius: 6px;
  padding: 2px 6px;
  line-height: 1.4;
}
.distance-gain {
  color: #2E7D32;
  background: rgba(76, 175, 80, 0.12);
}
.distance-loss {
  color: #C62828;
  background: rgba(244, 67, 54, 0.1);
}

/* ----- Toolbar layout ----- */
.rt-toolbar-spacer { flex: 1 1 auto; }

/* ----- Scope pill (extends header-pill-dropdown from element-variables) ----- */
.rt-scope-ico {
  display: inline-flex;
  margin-right: 6px;
  color: $muted;
}
.header-pill-dropdown.active .rt-scope-ico { color: $primary; }
.rt-scope-label {
  font-size: 12.5px;
  font-weight: 500;
}

/* ----- Scope menu (rendered inside el-dropdown popper).
   In this Element UI build the popper inherits the component's
   data-v scope attribute, so scoped styles reach it. We use ::v-deep
   to target el-dropdown internals; everything else stays scoped. */
::v-deep .rt-scope-section-label {
  font-size: 11px;
  font-weight: 700;
  color: #8a8a8a;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  padding: 10px 14px 6px;
}
::v-deep .rt-scope-divider {
  height: 1px;
  background: $line;
  margin: 6px 8px;
}
::v-deep .rt-scope-item-selected {
  color: $primary !important;
  background: rgba(108, 48, 204, 0.08) !important;
  font-weight: 500;
}
::v-deep .rt-month-picker {
  padding: 4px 8px 8px;
  min-width: 220px;
}
::v-deep .rt-year-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 8px 10px;
  font-size: 12.5px;
  font-weight: 500;
  color: $ink;
}
::v-deep .rt-year-arrow {
  cursor: pointer;
  padding: 0 6px;
  color: $muted;
  font-size: 16px;
  line-height: 1;
  &.disabled { opacity: 0.3; cursor: default; }
  &:hover:not(.disabled) { color: $primary; }
}
::v-deep .rt-year-label { font-weight: 600; }
::v-deep .rt-month-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 4px;
}
::v-deep .rt-month-cell {
  text-align: center;
  padding: 8px 0;
  border-radius: 6px;
  font-size: 12px;
  color: $ink;
  cursor: pointer;
  transition: background-color 0.15s ease;
  &:hover:not(.rt-month-cell--disabled) {
    background: rgba(108, 48, 204, 0.06);
  }
}
::v-deep .rt-month-cell--active {
  background: $primary !important;
  color: #fff !important;
  font-weight: 600;
}
::v-deep .rt-month-cell--disabled {
  color: rgba(26, 26, 46, 0.25);
  cursor: default;
}
</style>
